/*
Carte "Sélection de la semaine" de la homepage v5.
Attend un espace issu de la query homeQuery (champs plats, sans fragment).
*/
import { ORImageSpace as SpaceImage } from '@/core/image';
import { useTranslation } from '@/helpers/translate';
import { ucfirst } from '@/helpers/common';

function PinIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-was-ink">
      <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

export default function SelectionCard({ item, tx, featured }) {
  const { t } = useTranslation();
  if (!item) return null;

  const picture = item.pictures?.[0];
  // `type` peut contenir plusieurs slugs séparés par des virgules : on affiche le premier.
  const typeSlug = (item.type || '').split(',').map((v) => v.trim()).filter(Boolean)[0] || null;
  // Libellé du type : `space:type.<slug>` (celui des cartes de la recherche, cf. computeType),
  // sinon `space:types.<slug>`, sinon le slug capitalisé.
  const typeLabel = typeSlug
    ? [`space:type.${typeSlug}`, `space:types.${typeSlug}`]
      .map((key) => ({ key, value: t(key) }))
      .find(({ key, value }) => value && value !== key)?.value || ucfirst(typeSlug)
    : null;
  const surface = item.surface ? Math.round(item.surface) : null;
  const capacity = item.dataByCategory?.capacities?.capacity || null;
  const priceCorpo = item.dataByCategory?.corpoPrice?.amount_excl_tax;
  const pricePublic = item.dataByCategory?.price?.amount_excl_tax;
  const price = priceCorpo || pricePublic;
  const city = ucfirst(item.location?.city || item.location?.locality || '') || null;

  return (
    <a
      href={item.urlByLang}
      className="group flex h-full flex-col overflow-hidden rounded-xl border border-was-ink/8 bg-white text-was-ink transition duration-300 ease-out hover:border-was-ink/20 hover:text-was-ink"
    >
      <div className={`relative overflow-hidden bg-was-plum ${featured ? 'h-[300px] flex-1 md:min-h-[420px]' : 'h-[230px]'}`}>
        <div className="absolute inset-0 transition duration-700 ease-out group-hover:scale-[1.03]">
          {picture && <SpaceImage picture={picture} defaultAlt={item.title} cardsSpacesCities />}
        </div>
        {price && (
          <span className="absolute bottom-3 left-3 rounded-md bg-white/95 px-2.5 py-1 text-xs font-normal text-was-ink backdrop-blur-sm">
            {priceCorpo && priceCorpo !== pricePublic && <span className="mr-1.5 font-medium text-was-muted line-through">{pricePublic} €</span>}
            {price}
            {' € '}
            <span className="font-medium text-was-muted">{t('core:pricebyday')}</span>
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1.5 px-5 pb-5 pt-4">
        {typeLabel && (
          <span className="self-start text-xs font-normal text-was-muted">{typeLabel}</span>
        )}
        <h3 className={`m-0 line-clamp-2 font-bold leading-snug tracking-[-0.01em] ${featured ? 'text-lg' : 'text-[15px]'}`}>{item.title}</h3>
        <div className="flex items-center justify-between gap-3 text-[13px] text-was-muted">
          <span className="truncate">
            {surface && <strong className="font-bold text-was-ink">{`${surface} m²`}</strong>}
            {surface && capacity ? ' · ' : ''}
            {capacity ? `${capacity} ${tx('selection.persons')}` : ''}
          </span>
          {city && (
            <span className="inline-flex shrink-0 items-center gap-1 text-sm text-was-slate">
              <PinIcon />
              {city}
            </span>
          )}
        </div>
      </div>
    </a>
  );
}
